/**
 * Visibility's address (typed search params), so a door from Home or a review can open a campaign, a stage's panel
 * or Edit stages, and Back/Forward walk through them. Every param is optional and anything unrecognised is dropped,
 * so a hand-edited or stale link can never break the page.
 *
 * - `playbook`: the campaign on display (default: her first).
 * - `edit=stages`: Edit stages in place of the road.
 * - `stop`: the stop open in Edit stages (her phone's stop page); only with `edit=stages`.
 * - `stage`: open this stage's panel once (Home's stage rows); the page removes it after.
 * - `details`: open Campaign details once (Deals' "Edit this campaign’s details"); the page removes it after.
 * - `back`: only with `details` — the page Campaign details was opened from (Review Content, Team & roles, Integrations):
 *   its close leads back there ("Back to Review Content"). Only an address on Review or Settings is kept; a full URL,
 *   `//host` and any other path are dropped (`campaign-details/campaign-details-href.ts` detailsBackOf).
 * - `card=approvals`: only with `details` — Campaign details opens on its Approvals card. The page keeps `back` and
 *   `card` for the visit and removes them from the address with `details`.
 * - `from`: with `details` only — `change`, `add` or `also` (company rules round 5, F2a: "Back to Campaign details" from
 *   Settings): the control of "How Draper checks videos here" she pressed takes the focus back; removed with `details`.
 * - `section`: her phone — one section of the page open as its own page (L-phone §10.1: `about`, `waits`, `where`,
 *   `coming`, `deals`, `widgets`). Ignored on a laptop (kept, not stripped). Never with `edit=stages`.
 * - `sheet=more`: her phone — the More sheet is open (Back closes it). Never with a section open, nor while editing.
 *
 * A plain validator rather than zod on purpose: route `validateSearch` functions ship in the entry chunk, which the
 * sign-in page's budget pays for.
 */
import { isDetailsFrom, type DetailsFrom } from '@/features/settings/settings-search';
import { detailsBackOf, isDetailsCard, type DetailsCard } from './campaign-details/campaign-details-href';

export interface VisibilitySearch {
  playbook?: number;
  edit?: 'stages';
  stop?: string;
  stage?: string;
  details?: true;
  /** With `details` only: the address Campaign details leads back to. */
  back?: string;
  /** With `details` only: the card Campaign details opens on. */
  card?: DetailsCard;
  /** With `details` only: the control Campaign details puts the focus back on (F2a). */
  from?: DetailsFrom;
  section?: PhoneSectionId;
  sheet?: 'more';
}

/** Her phone's sections, in the list's order (L-phone §2.4). */
export const PHONE_SECTION_IDS = ['about', 'waits', 'where', 'coming', 'deals', 'widgets'] as const;
export type PhoneSectionId = (typeof PHONE_SECTION_IDS)[number];
export const isPhoneSectionId = (value: unknown): value is PhoneSectionId => PHONE_SECTION_IDS.some((id) => id === value);

/** What a link may pass. */
export type VisibilitySearchInput = Partial<VisibilitySearch>;

/** A stage's id as the layer writes it: a built-in bucket (`negotiations`) or one of hers (`custom_mg3k1x_1`). */
const STAGE_ID = /^[A-Za-z0-9_:-]{1,64}$/;

const positiveInt = (value: unknown): number | undefined => {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN;
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
};
const stageId = (value: unknown): string | undefined => (typeof value === 'string' && STAGE_ID.test(value) ? value : undefined);

export function parseVisibilitySearch(input: VisibilitySearchInput | Record<string, unknown>): VisibilitySearch {
  const raw = input as Record<string, unknown>;
  const out: VisibilitySearch = {};
  const playbook = positiveInt(raw.playbook);
  if (playbook) out.playbook = playbook;
  if (raw.edit === 'stages') {
    out.edit = 'stages';
    const stop = stageId(raw.stop);
    if (stop) out.stop = stop;
  }
  const stage = stageId(raw.stage);
  if (stage) out.stage = stage;
  if (out.edit !== 'stages' && (raw.details === true || raw.details === 'true')) {
    out.details = true;
    // Where Campaign details was opened from, and the card it opens on: only with `details`.
    const back = detailsBackOf(raw.back);
    if (back) out.back = back.to;
    if (isDetailsCard(raw.card)) out.card = raw.card;
  }
  if (out.details && isDetailsFrom(raw.from)) out.from = raw.from;
  // Her phone's pages: a section, or else the More sheet — neither while Edit stages is open.
  if (out.edit !== 'stages') {
    if (isPhoneSectionId(raw.section)) out.section = raw.section;
    else if (raw.sheet === 'more') out.sheet = 'more';
  }
  return out;
}
